import type ApiClient from './ApiClient';
import type ApiClientResponseInterceptor from '@/shared/api/api-client/api-client-interceptors/ApiClientResponseInterceptor';
import type ApiRequestOptions from '@/shared/api/api-request/ApiRequestOptions';

import { without } from 'lodash-es';

import createAxiosInstance from '@/shared/api/api-client/api-client-request/createAxiosInstance';
import requestViaAxios from '@/shared/api/api-client/api-client-request/requestViaAxios';
import ApiRequestMethod from '@/shared/api/api-request/ApiRequestMethod';

export default function createApiClient(clientOptions?: { baseUrl: string }): ApiClient {
  const axiosInstance = createAxiosInstance();

  let responseInterceptors: ApiClientResponseInterceptor[] = [];

  const request = async (url: string, options: ApiRequestOptions) =>
    requestViaAxios(axiosInstance, url, {
      ...options,
      baseUrl: clientOptions?.baseUrl,
      responseInterceptors,
    });

  return {
    delete: async (url, options) => {
      return request(url, {
        method: options?.method ?? ApiRequestMethod.DELETE,
        ...options,
      });
    },
    get: async (url, options) => {
      return request(url, {
        method: options?.method ?? ApiRequestMethod.GET,
        ...options,
      });
    },
    getBaseUrl: () => clientOptions?.baseUrl ?? null,
    patch: async (url, options) => {
      return request(url, {
        method: options?.method ?? ApiRequestMethod.PATCH,
        ...options,
      });
    },
    post: async (url, options) => {
      return request(url, {
        method: options?.method ?? ApiRequestMethod.POST,
        ...options,
      });
    },
    put: async (url, options) => {
      return request(url, {
        method: options?.method ?? ApiRequestMethod.PUT,
        ...options,
      });
    },
    request: async (url, options) => {
      return request(url, options ?? {});
    },
    useResponseInterceptor: (interceptor) => {
      responseInterceptors = [...responseInterceptors, interceptor];

      return () => {
        responseInterceptors = without(responseInterceptors, interceptor);
      };
    },
  };
}
